// The admin app's two global touch gestures, as pure decisions (owner spec
// 2026-10-01; plan › Shared contract):
//
//   • LEFT-EDGE SWIPE-BACK. An iPhone home-screen app has no native swipe-back,
//     so the shell provides one: a touch that STARTS within 24 px of the left
//     edge, dragged right, moves the page with the finger; let go past 35 % of
//     the width (or flick) and it goes back. Pages' own horizontal swipes must
//     ignore touches that start in that strip (same strip as the editor's
//     tools drawer, lib/edgeSwipeDrawer.ts).
//   • PULL-TO-REFRESH. At the top of the page, pull down → every active React
//     Query query is refetched. A scroller marked data-no-ptr never pulls.
//
// Everything that needs a decision is here and unit-tested; the hooks that
// bind the listeners only move pixels (components/appShell/AdminAppShell.tsx).
import { EDGE_ZONE_PX, isEdgeZoneTouch } from '@/lib/edgeSwipeDrawer';

export { EDGE_ZONE_PX };

/** Movement before a touch is judged horizontal or vertical. */
export const AXIS_SLOP_PX = 10;
/** Past this share of the width, letting go goes back. */
export const COMMIT_RATIO = 0.35;
/** px per ms: a flick this fast goes back even when short. */
export const FLICK_VELOCITY = 0.5;
const FLICK_MIN_PX = 40;
/** With nowhere to go back to, the page only gives this much. */
export const RUBBER_MAX_PX = 36;

export function startsSwipeBack(clientX: number): boolean {
  return isEdgeZoneTouch(clientX, EDGE_ZONE_PX);
}

export type Axis = 'pending' | 'horizontal' | 'vertical';

/** Rightward and clearly more sideways than up/down → horizontal; anything
 *  else past the slop belongs to the page's vertical scroll. */
export function axisLock(dx: number, dy: number, slop: number = AXIS_SLOP_PX): Axis {
  if (Math.abs(dx) < slop && Math.abs(dy) < slop) return 'pending';
  return dx > 0 && Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
}

export function swipeBackOffset(dx: number, canGoBack: boolean, width: number): number {
  if (dx <= 0) return 0;
  if (canGoBack) return Math.min(dx, width);
  // Rubber band: gives a little, then less and less.
  return RUBBER_MAX_PX * (1 - 1 / (dx / 60 + 1));
}

export function shouldCommitBack(p: { dx: number; vx: number; width: number; canGoBack: boolean }): boolean {
  if (!p.canGoBack) return false;
  if (p.dx >= p.width * COMMIT_RATIO) return true;
  return p.vx >= FLICK_VELOCITY && p.dx >= FLICK_MIN_PX;
}

/** Touch screens only — and not in an iPhone Safari TAB, where Safari's own
 *  edge swipe already goes back (both would fire: a double back). */
export function swipeBackEnabled(env: { touch: boolean; ios: boolean; standalone: boolean }): boolean {
  if (!env.touch) return false;
  return !(env.ios && !env.standalone);
}

/** React Router's BrowserRouter writes { idx } into history.state: above 0
 *  there is an in-app page behind this one. */
export function canGoBackInApp(state: unknown): boolean {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
}

// ── Pull-to-refresh ─────────────────────────────────────────────────────────
export const PTR_THRESHOLD_PX = 64;
export const PTR_MAX_PX = 96;

/** Finger distance → indicator distance (half speed, capped). */
export function ptrPull(dy: number): number {
  if (dy <= 0) return 0;
  return Math.min(PTR_MAX_PX, dy / 2);
}

function isVerticalScroller(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight) return false;
  const oy = getComputedStyle(el).overflowY;
  return oy === 'auto' || oy === 'scroll' || oy === 'overlay';
}

/**
 * A touch that starts here must not pull the page: inside a data-no-ptr
 * scroller (chat thread, map…), in a text field, in a dialog/sheet, or inside
 * an inner scroller that is not at its own top (the person is scrolling IT).
 */
export function ptrBlocked(target: Element | null): boolean {
  if (!target) return true;
  if (target.closest('[data-no-ptr], [role="dialog"], [aria-modal="true"], textarea, input, select, [contenteditable="true"]')) return true;
  for (let el: Element | null = target; el && el !== document.body; el = el.parentElement) {
    if (el instanceof HTMLElement && el.scrollTop > 0 && isVerticalScroller(el)) return true;
  }
  return false;
}
